/**
 * How much of the screen a reader can actually see, and what a phone keyboard
 * is covering of it.
 *
 * One module because two things need the same answer and must not disagree
 * about it: `useKeyboardAvoidance`, which scrolls the field being typed into
 * clear of the keyboard, and `useAnchoredPanel`, which decides where a
 * dropdown's list is allowed to open. A form where the field is lifted clear
 * and the list opens behind the keyboard anyway is worse than one where
 * neither moved.
 *
 * A keyboard does not make the page shorter. It slides over the bottom of it:
 * the layout viewport the page was laid out in stays exactly as tall as it was
 * and the *visual* viewport — the strip still on screen — shrinks. Both are
 * measured here from the top of the layout viewport, because that is what
 * `getBoundingClientRect` measures from and what a `position: fixed` element
 * is placed against, so the three are comparable without conversion.
 */

export interface Band {
  top: number;
  bottom: number;
}

/**
 * How much has to be covered before it is taken for a keyboard.
 *
 * A URL bar sliding away moves both viewports together and shows up as nothing
 * at all; this is insurance against the browsers where it shows up as a few
 * dozen pixels.
 */
export const KEYBOARD_MIN = 120;

/** The controls a keyboard actually opens for. */
export const TYPED_INTO =
  "input:not([type='checkbox']):not([type='radio']):not([type='button'])" +
  ":not([type='submit']):not([type='reset']),textarea,[contenteditable='true']";

/**
 * What a keyboard is assumed to be covering where the browser will not say, as
 * a share of the window.
 *
 * Half is more than any phone keyboard takes in portrait — most land between
 * 40% and 47% with their suggestion strip — and being wrong this way costs a
 * field sitting higher up the screen than it needed to. Being wrong the other
 * way costs the thing this is here to prevent: typing into a box you cannot
 * see. So it rounds up.
 */
const ASSUMED_SHARE = 0.5;

/**
 * A device whose keyboard is on the screen rather than under the hands.
 *
 * The guess below must never be made anywhere else. A desktop has a text field
 * focused for most of the time anybody is filling a form in, and half its
 * window written off as covered would open every dropdown in the dashboard
 * halfway up the screen. jsdom answers nothing, which counts as a desktop —
 * the same reading `DesktopOnly` takes of the same silence.
 */
const onGlass = (): boolean =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(hover: none) and (pointer: coarse)").matches;

/** Whether the caret is in something a keyboard would have opened for. */
const typing = (): boolean => {
  const field = document.activeElement;
  return field instanceof HTMLElement && field.matches(TYPED_INTO);
};

/**
 * The tallest the window has been at this width, which is the window with no
 * keyboard over it.
 *
 * Remembered per width so turning the phone starts the measurement again
 * rather than comparing a portrait height against a landscape one. Read on
 * every measurement, so it is current without a listener of its own.
 */
let widest = { width: 0, height: 0 };

/**
 * Whether this browser has ever said, of its own accord, that a keyboard was
 * up — by shrinking its visual viewport, or by shrinking the window itself.
 *
 * A browser that has answered once answers every time, and from then on it is
 * the only thing worth listening to. Nothing may be assumed over the top of
 * it, and this is what stops that happening: a keyboard put away with the
 * caret still in the field is a keyboard the browser reports as gone and the
 * guess below would go on insisting was there — a screen's worth of room held
 * open under a form that no longer needs any, which is what it looked like.
 *
 * One way only. A browser does not stop reporting once it has started, and a
 * moment where nothing is up is not evidence that nothing is ever said.
 */
let reportsKeyboards = false;

/**
 * The deepest a finger has landed since the caret last moved, measured from
 * the top of the window.
 *
 * This is the one thing a browser cannot keep to itself. A touch that reaches
 * the page at all is a touch that did not land on a keyboard, so wherever it
 * lands, the keyboard's top edge is below it — and a browser that reports
 * nothing has still just said, by letting the touch through, how much of the
 * screen is at most covered.
 *
 * Which is the answer to the one case nothing else can reach: a keyboard put
 * away with its own key, in a browser that will not mention it. The room made
 * for it stays open, blank, above the Submit bar, until something proves it is
 * no longer needed. The next touch anywhere in the lower half of the screen is
 * that proof, and it arrives the moment the reader reaches for the form again.
 *
 * Forgotten whenever the caret moves into a field, because that is the moment
 * a keyboard may be about to appear — and the tap that opened the field is
 * very often the deepest touch of the lot.
 */
let touchedTo = 0;

const uncoveredHeight = (): number => {
  if (window.innerWidth !== widest.width) {
    widest = { width: window.innerWidth, height: window.innerHeight };
  } else if (window.innerHeight > widest.height) {
    widest.height = window.innerHeight;
  }
  return widest.height;
};

/**
 * What the keyboard is covering where the browser has not said so itself.
 *
 * There are three kinds of browser here and only the first two answer.
 *
 * Most report it: the visual viewport shrinks, by exactly the height of the
 * keyboard, and `visibleBand` reads it straight off. A few shrink the *window*
 * instead — the Android webviews that resize rather than overlay — and those
 * have answered too, in their own way: nothing is hidden, the page is simply
 * shorter than it was, and the strip on screen is the whole of it.
 *
 * And then there are the in-app browsers — the one inside Instagram, the one
 * inside Facebook, which between them are most of how a shared form link is
 * ever opened. Some of them report neither. The keyboard slides up, the page
 * is not told, and every measurement here says the field at the bottom of the
 * screen is in plain sight while somebody types into it blind. That is the
 * case this exists for: a window still at its full height with the caret in a
 * field is a window with something over it that has not been declared, and
 * half of it is the safe guess.
 */
const assumedCovering = (): number => {
  // This browser answers for itself, and has: what it says now is that there
  // is no keyboard up, whatever the caret is sitting in.
  if (reportsKeyboards) return 0;
  if (!onGlass() || !typing()) return 0;

  // The window itself shrank, so the keyboard is already accounted for — and
  // that is an answer too, which is why it is remembered as one.
  if (uncoveredHeight() - window.innerHeight >= KEYBOARD_MIN) {
    reportsKeyboards = true;
    return 0;
  }

  const assumed = Math.round(window.innerHeight * ASSUMED_SHARE);

  // The most that can still be covered given where the last finger landed.
  // Never more than the guess: a touch high up the screen proves nothing about
  // what is below it, and this may only ever revise the guess downward.
  const proven = touchedTo > 0 ? Math.max(0, window.innerHeight - touchedTo) : assumed;

  return Math.min(assumed, proven);
};

/**
 * Where a finger has just landed, in window coordinates — see `touchedTo`.
 *
 * Handed the point rather than reading it here, so this stays a measurement of
 * the screen with no listeners of its own; `useKeyboardAvoidance` is what
 * watches for touches, on the screens where a keyboard is a thing that
 * happens.
 *
 * Answers whether it told us anything we did not already know, so a drag
 * across half the screen only costs a re-measurement on the frames where it
 * actually reached further down than before.
 */
export const noteTouch = (y: number): boolean => {
  if (y <= touchedTo) return false;
  touchedTo = y;
  return true;
};

/** Forgets where fingers have landed — the caret has moved, so a keyboard may
 *  be on its way up and nothing said before it counts. */
export const forgetTouches = (): void => {
  touchedTo = 0;
};

/**
 * The strip of the page a reader can see, in the page's own coordinates.
 *
 * The window, until a keyboard slides over the bottom of it — reported by the
 * browser where the browser reports it, and assumed where it does not. See
 * `assumedCovering`.
 */
/**
 * What the browser says its keyboard is covering, or nothing where it does not
 * say.
 *
 * Measured off the visual viewport's *height* against the window, and
 * deliberately not off where its bottom edge lands. Safari on iOS does both
 * things at once when a keyboard opens: it shortens the visual viewport, and it
 * slides that shorter strip down the layout viewport it sits in — `offsetTop`
 * goes positive by roughly as much as the keyboard took. So the strip's bottom
 * edge comes back out level with the bottom of the window, and a keyboard
 * measured from that edge measures as nothing at all.
 *
 * That is precisely what went wrong on an iPhone: a keyboard two hundred pixels
 * tall read as zero, the reading fell through to the guess, and the guess
 * clamped the visible strip to half a window that was already only half a
 * window — leaving a sheet squeezed to its floor of 96px with one option in it,
 * cut off halfway.
 *
 * The height is the honest figure in both browsers. Where the strip *is* on the
 * screen is `visibleBand`'s business, and `offsetTop` is how that is answered.
 */
const reportedCovering = (): number => {
  const viewport = window.visualViewport;
  if (!viewport) return 0;

  // Pinching in shrinks the visual viewport too, and is not a keyboard.
  if (viewport.scale > 1) return 0;

  const covered = window.innerHeight - viewport.height;
  if (covered < KEYBOARD_MIN) return 0;

  reportsKeyboards = true;
  return covered;
};

/**
 * What a keyboard is covering, however that had to be arrived at — the
 * browser's own figure where there is one, and `assumedCovering` where there
 * is not.
 *
 * This is the number that decides how much room is opened below the page for a
 * field to be scrolled up into. It is not `innerHeight - visibleBand().bottom`:
 * on iOS those two are different questions with different answers, for the
 * reason set out above.
 */
export const keyboardCovering = (): number =>
  reportedCovering() || assumedCovering();

/**
 * Whether this browser has ever reported a keyboard of its own accord — see
 * `reportsKeyboards`.
 *
 * Asked by `useKeyboardAvoidance`, because a browser that reports its keyboard
 * is also a browser that scrolls for it, and the two of them scrolling for the
 * same keyboard is a fight the reader feels.
 */
export const browserReportsKeyboard = (): boolean => reportsKeyboards;

export const visibleBand = (): Band => {
  const viewport = window.visualViewport;
  const top = viewport?.offsetTop ?? 0;
  const reported = viewport ? top + viewport.height : window.innerHeight;

  // The browser has measured the strip itself: a visual viewport shorter than
  // the window is one with a keyboard over the rest, and what is left of it —
  // wherever it has been slid to — is exactly what can be seen.
  if (reportedCovering() > 0) return { top, bottom: reported };

  // Nothing reported. An assumed keyboard covers the foot of the *screen*, so
  // it comes off the bottom of the strip the browser does admit to rather than
  // off the bottom of the window.
  //
  // Those are the same edge in the browsers this guess is really for, where
  // the strip is the window. They come apart on iOS, which leaves the strip
  // slid down the layout viewport after a keyboard has been up once: taking a
  // window's worth of guess off a strip that starts most of a window down left
  // a band eighty pixels tall, and a sheet squeezed to its floor — the same
  // symptom as before, on the second opening rather than the first.
  return {
    top,
    bottom: Math.max(top, reported - assumedCovering()),
  };
};

/**
 * Forgets what this module has worked out about the browser it is running in.
 *
 * For tests, which is the only place one page is several browsers in a row. A
 * real one is a single browser for as long as it is open, and both the height
 * it settles at and whether it reports its keyboard are facts about it that do
 * not change under anybody's feet.
 */
export const forgetViewport = (): void => {
  widest = { width: 0, height: 0 };
  reportsKeyboards = false;
  touchedTo = 0;
};
